import { EMonetaryAmountBy } from "../../../invoice/create/interfaces/invoice.interfaces";
import { normalizeCostOverride } from "../../jobs-list/jobs-layout/components/Summary/BasicJob";
import type { Job } from "../../interfaces/job.interfaces";
import type { ScopeOfWork } from "../../../scope-of-work/types/scope-of-work.interface";
import { resolveJobServiceCost } from "./resolveJobServiceCost";

export type JobPricingFormValues = {
  by: string;
  price: string;
  units: string;
  currency: string;
};

const FLAT_FEE_TYPES = new Set([
  EMonetaryAmountBy.UNIQUE,
  EMonetaryAmountBy.MONTHLY,
  EMonetaryAmountBy.YEARLY,
  "unique",
  "monthly",
  "yearly",
]);

const ADVANCED_PRICING_VALUES = new Set<string>(Object.values(EMonetaryAmountBy));

const BASIC_MEASURE_VALUES = new Set([
  "sqft",
  "sqm",
  "minutes",
  "hours",
  "days",
  "weeks",
  "months",
  EMonetaryAmountBy.PER_ITEM,
  EMonetaryAmountBy.UNIQUE,
]);

function normalizePricingBy(rawBy: unknown, isAdvanced: boolean): string {
  const by = String(rawBy ?? "").toLowerCase().trim();
  const allowed = isAdvanced ? ADVANCED_PRICING_VALUES : BASIC_MEASURE_VALUES;

  if (allowed.has(by)) return by;

  const aliases: Record<string, string> = {
    one_time: EMonetaryAmountBy.UNIQUE,
    onetime: EMonetaryAmountBy.UNIQUE,
    flat: EMonetaryAmountBy.UNIQUE,
    per_sqft: EMonetaryAmountBy.PER_SQUARE_FOOT,
    per_sf: EMonetaryAmountBy.PER_SQUARE_FOOT,
    square_foot: EMonetaryAmountBy.PER_SQUARE_FOOT,
    unit: EMonetaryAmountBy.PER_ITEM,
  };

  const mapped = aliases[by];
  if (mapped && allowed.has(mapped)) return mapped;

  return isAdvanced ? EMonetaryAmountBy.UNIQUE : "sqft";
}

export function isFlatFeePricingType(by?: string | null): boolean {
  if (!by) return false;
  return FLAT_FEE_TYPES.has(by);
}

export function extractJobPricingDefaults(
  job: Job,
  scopeOfWork?: ScopeOfWork | null,
): JobPricingFormValues {
  const costOverride = normalizeCostOverride(scopeOfWork?.costOverride) as Record<string, unknown> | null;

  const isAdvanced = job.type === "advanced";

  if (costOverride) {
    const price = costOverride.price ?? costOverride.value ?? costOverride.amount ?? "";
    return {
      by: normalizePricingBy(costOverride.by, isAdvanced),
      price: price === "" || price == null ? "" : String(price),
      units: costOverride.units == null || costOverride.units === "" ? "" : String(costOverride.units),
      currency: String(costOverride.currency ?? "USD"),
    };
  }

  const serviceCost = resolveJobServiceCost(job, scopeOfWork);
  if (serviceCost) {
    const inferredBy = isAdvanced
      ? normalizePricingBy(
          serviceCost.unitLabel?.toLowerCase().includes("sq")
            ? EMonetaryAmountBy.PER_SQUARE_FOOT
            : EMonetaryAmountBy.UNIQUE,
          true,
        )
      : serviceCost.unitLabel?.toLowerCase().includes("sq")
        ? EMonetaryAmountBy.PER_SQUARE_FOOT
        : "sqft";

    return {
      by: inferredBy,
      price: serviceCost.unitPrice != null ? String(serviceCost.unitPrice) : String(serviceCost.amount),
      units: serviceCost.units != null ? String(serviceCost.units) : "",
      currency: serviceCost.currency ?? "USD",
    };
  }

  return {
    by: job.type === "advanced" ? EMonetaryAmountBy.UNIQUE : "sqft",
    price: "",
    units: "",
    currency: "USD",
  };
}
